import type { ReactNode } from 'react'
import type { EvDecision } from '../domain/types'
import type { Explainability, ScoredOpportunity } from '../lib/math'
import type { BadgeProgress, DegenerateVerdict, GreedMeter } from '../lib/behavior'
import { fmtPct, fmtSigned } from '../lib/format'
import { Gauge } from './charts'
import { Banner, Button, Card, Pill, type Tone } from './ui'

const ACTION_META: Record<EvDecision, { tone: Tone; label: string }> = {
  strong: { tone: 'green', label: 'Strong watch' },
  small: { tone: 'blue', label: 'Small position' },
  paper: { tone: 'amber', label: 'Paper only' },
  skip: { tone: 'red', label: 'Skip' },
}

export function ActionPill({ action }: { action: EvDecision }) {
  const m = ACTION_META[action]
  return <Pill tone={m.tone}>{m.label}</Pill>
}

const KIND_TONE: Record<string, Tone> = { stock: 'blue', prediction: 'purple', sportsbook: 'amber' }

export function KindPill({ kind }: { kind: string }) {
  return <Pill tone={KIND_TONE[kind] ?? 'gray'}>{kind}</Pill>
}

export function GreedMeterCard({ greed }: { greed: GreedMeter }) {
  const tone: Tone = greed.level === 'tilt' ? 'red' : greed.level === 'hot' ? 'amber' : greed.level === 'warm' ? 'amber' : 'green'
  return (
    <Card title="Greed Meter" subtitle="Discipline gauge across risk, sizing and streaks">
      <Gauge value={greed.score} />
      <div className="center" style={{ marginTop: 4 }}>
        <Pill tone={tone}>{greed.level.toUpperCase()}</Pill>
      </div>
      <p className="muted center" style={{ marginTop: 10, marginBottom: 0 }}>
        {greed.message}
      </p>
    </Card>
  )
}

export function DegenerateBanner({ verdict }: { verdict: DegenerateVerdict }) {
  if (!verdict.degenerate) {
    return (
      <Banner tone="info" icon={'\u2713'}>
        <strong>Degenerate Detector: clear.</strong> No overtrading or chasing patterns detected. Keep it boring.
      </Banner>
    )
  }
  return (
    <Banner tone="danger" icon={'\uD83D\uDEA8'}>
      <strong>Degenerate Detector tripped (score {verdict.score}).</strong>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
        {verdict.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
    </Banner>
  )
}

const BADGE_EMOJI: Record<string, string> = {
  'took-a-skip': '\uD83D\uDED1',
  'under-risk-limit': '\uD83D\uDEE1\uFE0F',
  'logged-10': '\uD83D\uDCD3',
  'avoided-tilt': '\uD83E\uDDD8',
  'beat-closing-line': '\uD83C\uDFAF',
  'killed-bad-thesis': '\u2702\uFE0F',
}

export function BadgeShelf({ badges }: { badges: BadgeProgress[] }) {
  return (
    <div className="grid cols-3">
      {badges.map((b) => (
        <div key={b.id} className={`badge-tile ${b.earned ? 'earned' : ''}`}>
          <div className="badge-emoji">{BADGE_EMOJI[b.id] ?? '\uD83C\uDFC5'}</div>
          <div className="badge-name">{b.name}</div>
          <div className="badge-desc">{b.description}</div>
        </div>
      ))}
    </div>
  )
}

export function WarningList({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) {
    return (
      <Banner tone="info" icon={'\u2713'}>
        No active risk alerts. Bankroll, exposure and limits look healthy.
      </Banner>
    )
  }
  return (
    <div className="stack">
      {warnings.map((w, i) => (
        <Banner key={i} tone={i === 0 ? 'danger' : 'warn'}>
          {w}
        </Banner>
      ))}
    </div>
  )
}

export function OpportunityCard({ opp, footer }: { opp: ScoredOpportunity; footer?: ReactNode }) {
  return (
    <div className="card">
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <KindPill kind={opp.input.kind} />
            <strong>{opp.input.label}</strong>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Model {fmtPct(opp.input.modelProb)} vs market {fmtPct(opp.marketProb)} · score {(opp.score * 100).toFixed(0)}
          </div>
        </div>
        <ActionPill action={opp.action} />
      </div>
      <div className="row wrap" style={{ gap: 14, marginTop: 10 }}>
        <Metric label="Edge" value={fmtSigned(opp.edge * 100, 1) + '%'} tone={opp.edge > 0 ? 'pos' : 'neg'} />
        <Metric label="EV / unit" value={fmtSigned(opp.evPerUnit, 3)} tone={opp.evPerUnit > 0 ? 'pos' : 'neg'} />
        <Metric label="Confidence" value={fmtPct(opp.input.confidence, 0)} />
        <Metric label="Liquidity" value={fmtPct(opp.input.liquidity, 0)} />
      </div>
      {opp.riskyDespiteEdge && (
        <div style={{ marginTop: 10 }}>
          <Pill tone="amber">{'\u26A0'} Risky despite positive EV</Pill>
        </div>
      )}
      {opp.noTrade.skip && (
        <ul className="muted" style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12 }}>
          {opp.noTrade.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {footer && <div style={{ marginTop: 12 }}>{footer}</div>}
    </div>
  )
}

function Metric({ label, value, tone }: { label: string; value: ReactNode; tone?: 'pos' | 'neg' }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className={`mono ${tone ?? ''}`} style={{ fontSize: 15 }}>
        {value}
      </div>
    </div>
  )
}

export function ExplainPanel({ explain }: { explain: Explainability }) {
  const verdictTone: Tone = explain.verdict === 'trade-worthy' ? 'green' : explain.verdict === 'watchlist-only' ? 'amber' : 'red'
  return (
    <div className="stack">
      <div>
        <Pill tone={verdictTone}>Verdict: {explain.verdict}</Pill>
      </div>
      <ExplainList title="Data used" items={explain.dataUsed} />
      <ExplainList title="Assumptions" items={explain.assumptions} />
      <ExplainList title="What could go wrong" items={explain.whatCouldGoWrong} tone="warn" />
      <div>
        <div className="uppercase faint">Why the model may be wrong</div>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          {explain.whyModelMayBeWrong}
        </p>
      </div>
      <div>
        <div className="uppercase faint">What would invalidate this</div>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          {explain.whatInvalidates}
        </p>
      </div>
    </div>
  )
}

function ExplainList({ title, items, tone }: { title: string; items: string[]; tone?: 'warn' }) {
  return (
    <div>
      <div className={`uppercase faint`}>{title}</div>
      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }} className={tone === 'warn' ? 'warn' : 'muted'}>
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  )
}

export function RedTeamPanel({ args, onClose }: { args: string[]; onClose?: () => void }) {
  return (
    <Card title={'\uD83D\uDD25 Red Team My Idea'} action={onClose && <Button size="sm" onClick={onClose}>Close</Button>}>
      <p className="muted" style={{ marginTop: 0 }}>
        The strongest case against your trade. If you can't answer these, you don't have an edge.
      </p>
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {args.map((a, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {a}
          </li>
        ))}
      </ul>
    </Card>
  )
}
